const blockCustomerScrollToNav = {

    /**
     * the wp block class
     * @type {string}
     */
    containerClass: '.block__scrollto-nav',
    /**
     * the ul element in the block
     * @type {string}
     */
    listClass: '.scrollnav',
    /**
     * collection of the <a> elements in the nav
     * @type {NodeList} 
     */
    navItems: null,
    /**
     * flag to prevent multiple calls to the update function
     * @type {boolean}
     */
    updateCalled: false,

    /**
     * flag to toggle the nav open state
     * @type {boolean}
     */
    navOpen: false,

    /**
     * the button to toggle the nav
     * @type {HTMLElement}
     */
    toggleButton: null,

    // init function
    init() {
        // check if the nav exists, exit if not
        const nav = document.querySelector(`${this.containerClass} ${this.listClass}`)
        if (nav === null) {
            this.debug('Nav with this selector not found:', this.containerClass + ' ' + this.listClass)
            return
        }

        this.setNavItems()

        // exit if no nav items are found
        if (this.navItems.length === 0) {
            this.debug('No nav items found')
            return
        }

        // remove items which targets don't exist
        this.removeMissingTargets()

        // exit if no items are left
        if (this.navItems.length === 0) {
            this.debug('No nav items left after removing missing targets')
            return
        }

        // initial call to set the active item
        this.update()

        // register the scroll event
        window.addEventListener('scroll', () => this.onScroll())

        // handle the click of the <button> in .scrollnav to show all items
        this.toggleButton = document.querySelector(`${this.containerClass} button`)
        if (this.toggleButton) {
            this.toggleButton.addEventListener('click', () => {
                this.toggleNav()
                this.toggleButton.classList.toggle('rotate-180')
            })
        }
    },

    /**
     * Log a message to the console, but only if the hostname is not a dev
     * environment
     * @param  {...any} value
     * @returns {void}
     */
    debug(...value) {

        if (
            document.location.hostname.indexOf('adwerba') === -1 ||
            document.location.hostname.indexOf('test') === -1 ||
            document.location.hostname.indexOf('localhost') === -1
        ) {
            console.log('[ Scrollto-Nav ]', ...value)
        }

    },

    // look up all nav links in the DOM
    setNavItems() {
        this.navItems = document.querySelectorAll(`${this.containerClass} ${this.listClass} li a`)
    },

    /**
     * remove items which targets don't exist
     * @returns {void}
     */
    removeMissingTargets() {
        this.navItems.forEach(item => {
            const target = document.getElementById(this.getItemTarget(item))
            if (!target) {
                // remove the items parent li
                item.parentElement.remove()
                this.debug('Page section not found, nav item removed:', this.getItemTarget(item))
            }
        })

        // now that the missing ones are removed from the dom, set them again
        this.setNavItems()
    },

    /**
     * uses requestAnimationFrame to throttle the scroll event 
     * @returns {void}
     */
    onScroll() {
        // prevent multiple calls
        if (this.updateCalled) {
            return
        }

        this.updateCalled = true
        this.navOpen = false
        this.toggleButton.classList.remove('rotate-180')

        // use requestAnimationFrame to throttle the scroll event
        requestAnimationFrame(() => {
            this.update()
            this.updateCalled = false
        })
    },

    /**
     * make an item visible if its target is in the viewport
     * @returns {void}
     */
    update() {
        this.navItems.forEach(item => {
            const target = document.getElementById(this.getItemTarget(item))
            if (target && this.isInViewport(target)) {
                this.setActive(item)
            } else {
                this.setInactive(item, true)
            }
        })
    },

    /**
     * @param {HTMLElement} item - the nav item to set active
     * @returns {void}
     */
    setActive(item, force = false) {
        item.parentNode.classList.add('marker:text-primary')
        item.parentNode.classList.remove('marker:text-input-border')
        // text
        item.classList.remove('md:opacity-0', 'hidden', 'md:inline-block')
        item.classList.add('md:opacity-100')
        if (force) {
            item.parentNode.classList.add('w-auto')
            item.parentNode.classList.remove('w-0')
            item.classList.add('opacity-100', 'w-auto')
            item.classList.remove('opacity-0', 'w-0')
        }
    },

    /**
     * @param {HTMLElement} item - the nav item to set inactive
     * @returns {void}
     */
    setInactive(item) {
        // circle
        item.parentNode.classList.remove('marker:text-primary', 'w-auto')
        item.parentNode.classList.add('marker:text-input-border', 'w-0')
        // a element
        item.classList.remove('opacity-100', 'md:opacity-100', 'w-auto')
        item.classList.add('opacity-0', 'md:opacity-0', 'w-0', 'hidden', 'md:inline-block')
    },

    /**
     * Toggle all nav items (used for mobile switch)
     * 
     * Calls setActive if nav is closed, and setInactive if nav is open
     * @returns {void}
     */
    toggleNav() {
        this.navItems.forEach(item => {
            if (this.navOpen) {
                this.setInactive(item)
            } else {
                this.setActive(item, true)
            }
        })
        this.navOpen = !this.navOpen
    },

    /**
     * check if an element is in the viewport of the current scroll position
     * @param {HTMLElement} item
     * @returns {boolean}
     */
    isInViewport(item) {
        const rect = item.getBoundingClientRect()
        // check if more than 50% of the element is visible
        return rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2
    },

    /**
     * get the target of an item
     * @param {HTMLElement} item
     * @returns {string} the target id located in the data-target attribute
     */
    getItemTarget: (item) => item.dataset.target,
}

blockCustomerScrollToNav.init()